import { cn } from "@acme/ui";
import { Card } from "@acme/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@acme/ui/tabs";
import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { FileRoutesByTo } from "~/routeTree.gen.js";
import {
  canReadClanPlayerSearch,
  canReadClanPlayerSearchResults,
  getFirstVisiblePlayerSearchSubTabRoute,
} from "~/features/clan-detail/_utils/clan-tab-routes.js";
import { useClanSearchAuth } from "../route.js";

export const Route = createFileRoute("/_authed/my-clans/$tag/player-search")({
  component: PlayerSearchLayout,
});

const SUB_TABS = [
  {
    value: "filters",
    to: "/my-clans/$tag/player-search/filters",
    labelKey: "searchResults.tabs.filters",
  },
  {
    value: "results",
    to: "/my-clans/$tag/player-search/results",
    labelKey: "searchResults.tabs.results",
  },
] as const satisfies {
  value: "filters" | "results";
  to: keyof FileRoutesByTo;
  labelKey: string;
}[];

function PlayerSearchLayout() {
  const { t } = useTranslation();
  const { tag } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { effectivePermissions, isReady, canAccessClan } = useClanSearchAuth();

  const visibleSubTabs = SUB_TABS.filter((tab) => {
    if (tab.value === "filters") return canReadClanPlayerSearch(effectivePermissions);
    return canReadClanPlayerSearchResults(effectivePermissions);
  });

  const activeTab =
    visibleSubTabs.find((tab) =>
      tab.value === "results"
        ? location.pathname.endsWith("/results")
        : location.pathname.includes("/filters")
    )?.value ??
    visibleSubTabs[0]?.value ??
    "filters";

  useEffect(() => {
    if (!isReady || !canAccessClan) return;
    const onFilters = location.pathname.includes("/filters");
    const onResults = location.pathname.endsWith("/results");
    if (!onFilters && !onResults) return;

    if (onFilters && !canReadClanPlayerSearch(effectivePermissions)) {
      void navigate({
        to: getFirstVisiblePlayerSearchSubTabRoute(effectivePermissions),
        params: { tag },
        replace: true,
      });
      return;
    }

    if (onResults && !canReadClanPlayerSearchResults(effectivePermissions)) {
      void navigate({
        to: getFirstVisiblePlayerSearchSubTabRoute(effectivePermissions),
        params: { tag },
        replace: true,
      });
    }
  }, [canAccessClan, effectivePermissions, isReady, location.pathname, navigate, tag]);

  if (!isReady || !canAccessClan) return null;

  if (visibleSubTabs.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-center text-muted-foreground py-6">{t("app.clanDetail.noPermission")}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs
        onValueChange={(value) => {
          const tab = visibleSubTabs.find((item) => item.value === value);
          if (tab == null) return;
          void navigate({ to: tab.to, params: { tag } });
        }}
        value={activeTab}>
        <TabsList className={cn("h-auto flex-wrap")}>
          {visibleSubTabs.map(({ value, labelKey }) => (
            <TabsTrigger key={value} value={value}>
              {t(labelKey)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Outlet />
    </div>
  );
}
